import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import SearchAndFilters from './SearchAndFilters';
import UnifiedProgramCard from '../cards/UnifiedProgramCard';
import { EmptyStates, LoadingStates, ErrorStates } from './EmptyStates';
import SummerAdventureHero from './SummerAdventureHero';
import ResponsiveAdsterraAd from '@/components/ads/AdsterraInlineAds';
import FeaturedSponsor from '@/components/ads/FeaturedSponsor';
import { useUserTier } from '@/hooks/useUserTier';

/**
 * My Programs (My Camps) Dashboard
 * Comprehensive dashboard for viewing, filtering, searching, and managing camp programs
 */
const MyCampsDashboard = ({ 
  camps = [], 
  children = [], 
  onAddCamp, 
  onDiscover,
  onEditCamp, 
  onRemoveCamp,
  onView,
  onWhosIn,
  onCancelBooking
}) => {
  const { user } = useAuth();
  const { tier } = useUserTier();
  const shouldShowAds = tier === 'free';
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter and search state
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date-added');
  
  // Pre-parse dates for camps to avoid re-parsing on every filter/sort
  const campsWithParsedDates = useMemo(() => {
    return camps.map(camp => {
      let parsedStartDate = null;
      let parsedEndDate = null;
      
      if (camp.campStartDate) {
        if (camp.campStartDate instanceof Date) {
          parsedStartDate = camp.campStartDate;
        } else if (typeof camp.campStartDate === 'string') {
          // Handle ISO date strings
          if (/^\d{4}-\d{2}-\d{2}/.test(camp.campStartDate)) {
            const [year, month, day] = camp.campStartDate.split('-').map(Number);
            parsedStartDate = new Date(year, month - 1, day);
          } else {
            parsedStartDate = new Date(camp.campStartDate);
          }
        }
      }
      
      if (camp.campEndDate) {
        if (camp.campEndDate instanceof Date) {
          parsedEndDate = camp.campEndDate;
        } else if (typeof camp.campEndDate === 'string') {
          if (/^\d{4}-\d{2}-\d{2}/.test(camp.campEndDate)) {
            const [year, month, day] = camp.campEndDate.split('-').map(Number);
            parsedEndDate = new Date(year, month - 1, day);
          } else {
            parsedEndDate = new Date(camp.campEndDate);
          }
        }
      }
      
      return {
        ...camp,
        _parsedStartDate: parsedStartDate,
        _parsedEndDate: parsedEndDate
      };
    });
  }, [camps]);

  // Filter and sort camps - using memoized camps with parsed dates
  const filteredCamps = useMemo(() => {
    let filtered = [...campsWithParsedDates];

    // Status filter
    if (activeTab !== 'all') {
      filtered = filtered.filter(camp => 
        camp.status && camp.status.includes(activeTab)
      );
    }

    // Search filter (guard null/undefined name, organization, location)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(camp => {
        const name = (camp.name ?? '').toLowerCase();
        const organization = (camp.organization ?? '').toLowerCase();
        const location = (camp.location ?? '').toLowerCase();
        return name.includes(query) || organization.includes(query) || location.includes(query);
      });
    }

    // Sort - use pre-parsed dates when available
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'alphabetical':
          return (a.name ?? '').localeCompare(b.name ?? '');
        case 'camp-date':
          const aDate = a._parsedStartDate || (a.campStartDate ? new Date(a.campStartDate) : new Date(0));
          const bDate = b._parsedStartDate || (b.campStartDate ? new Date(b.campStartDate) : new Date(0));
          return aDate.getTime() - bDate.getTime();
        case 'price-low':
          return (a.regularPrice || 0) - (b.regularPrice || 0);
        case 'price-high':
          return (b.regularPrice || 0) - (a.regularPrice || 0);
        case 'date-added':
        default:
          return new Date(b.id) - new Date(a.id); // Using ID as proxy for date added
      }
    });

    return filtered;
  }, [campsWithParsedDates, activeTab, searchQuery, sortBy]);



  // Event handlers - memoized with useCallback
  const handleStatusClick = useCallback((status) => {
    setActiveTab(status);
  }, []);

  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const handleSortChange = useCallback((sort) => {
    setSortBy(sort);
  }, []);




  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSortBy('date-added');
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleRetry = () => {
    // Reset error state - parent component should handle retry
    setError(null);
  };

  // Calculate active filter count with useMemo
  const activeFilterCount = useMemo(() => {
    return [
      searchQuery,
      sortBy !== 'date-added'
    ].filter(Boolean).length;
  }, [searchQuery, sortBy]);

  // Check if user has no camps
  const hasNoCamps = camps.length === 0 && !isLoading && !error;

  // Render content based on state
  const renderContent = () => {
    if (error) {
      return (
        <ErrorStates 
          type="load-error" 
          onRetry={handleRetry}
          error={error}
        />
      );
    }

    if (isLoading) {
      return <LoadingStates type="camps-list" count={6} />;
    }

    // If no camps exist, don't render empty state here (hero card is shown at top level)
    if (hasNoCamps) {
      return null;
    }

    if (filteredCamps.length === 0) {
      if (searchQuery || activeFilterCount > 0) {
        return (
          <EmptyStates 
            type="no-search-results"
            onClearSearch={handleClearSearch}
            onClearFilters={handleClearFilters}
            onAddCamp={onAddCamp}
            onDiscover={onDiscover}
          />
        );
      }
      
        return (
          <EmptyStates 
            type="no-camps"
            activeTab={activeTab}
            onAddCamp={onAddCamp}
            onDiscover={onDiscover}
          />
        );
    }

    // Render camps in list view (skip falsy entries so UnifiedProgramCard never receives undefined)
    return (
      <div className="space-y-4">
        {filteredCamps.filter(camp => camp != null).map((camp, index) => {
          // Determine view mode: management if booked or has assigned children, otherwise saved
          const isBooked = camp.status && (Array.isArray(camp.status) ? camp.status.includes('booked') : camp.status === 'booked');
          const hasAssignedChildren = camp.assignedChildren && camp.assignedChildren.length > 0;
          const viewMode = (isBooked || hasAssignedChildren || camp.regularPrice) ? 'management' : 'saved';
          
          return (
            <React.Fragment key={camp.id}>
              <UnifiedProgramCard
                program={camp}
                viewMode={viewMode}
                variant="horizontal"
                isSaved={true}
                hideCategoryBadge={true}
                statusInlineWithTitle={true}
                uniformHeight={true}
                children={children}
                onClick={() => onView?.(camp)}
                onView={() => onView?.(camp)}
                onEdit={() => onEditCamp?.(camp)}
                onDelete={() => onRemoveCamp?.(camp)}
                onDirections={() => {}}
                onCancelBooking={() => onCancelBooking?.(camp)}
              />

              {/* Ad Placement - Only shown to free tier users
                  Responsive Adsterra ads: 300x250 mobile, 320x50 tablet, 728x90 desktop
              */}
              {shouldShowAds && index === 2 && (
                <section className="flex justify-center">
                  <div
                    className="
                      bg-white rounded-xl border border-slate-200/70 shadow-lg
                      px-3 py-2
                      mx-auto
                      w-[300px] h-[250px]
                      sm:w-[320px] sm:h-[50px]
                      lg:w-[728px] lg:h-[90px]
                      flex items-center justify-center
                    "
                    aria-label="Sponsored"
                  >
                    <ResponsiveAdsterraAd />
                  </div>
                </section>
              )}

              {shouldShowAds && (index === 7 || (index > 7 && (index - 7) % 5 === 0)) && (
                <section className="flex justify-center">
                  <div
                    className="
                      bg-white rounded-xl border border-slate-200/70 shadow-lg
                      px-3 py-2
                      mx-auto
                      w-[300px] h-[250px]
                      sm:w-[320px] sm:h-[50px]
                      lg:w-[728px] lg:h-[90px]
                      flex items-center justify-center
                    "
                    aria-label="Sponsored"
                  >
                    <ResponsiveAdsterraAd />
                  </div>
                </section>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className="pt-2 px-4 pb-4 lg:pt-3 lg:px-6 lg:pb-6 space-y-6">
          {/* Hero Card - Show when no camps exist */}
          {hasNoCamps && (
            <SummerAdventureHero 
              onDiscover={onDiscover}
              onAddCamp={onAddCamp}
            />
          )}

          {/* Featured Sponsor Ad - Only shown to free tier users when no camps */}
          {shouldShowAds && hasNoCamps && (
            <div className="mt-2">
              <FeaturedSponsor />
            </div>
          )}

          {/* Search and Filters (includes status filter dropdown to the right of Add a Camp) - Hide when no camps */}
          {!hasNoCamps && (
            <SearchAndFilters
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              sortBy={sortBy}
              onSortChange={handleSortChange}
              isLoading={isLoading}
              onAddCamp={onAddCamp}
              activeTab={activeTab}
              onStatusClick={handleStatusClick}
            />
          )}


          {/* Camp Content */}
          {renderContent()}
        </div>
  );
};

export default MyCampsDashboard;
